//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { ProductDetailsFieldLabels } from '@campaigns/components/shared/product-details-modal/drawer/constants/ProductDetailsModalFieldLabels';
import { FormField } from '@campaigns/components/shared/product-details-modal/drawer/form/FormField';
import { getVariationsValidationRules } from '@campaigns/constants/sampling/SamplingProductValidationRulesConstants';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { getRevenueSymbolFromFormat } from '@utils/readableNumber';

interface VariationBasicFieldsProps {
  isViewOnly: boolean;
}

export const VariationBasicFields = ({
  isViewOnly,
}: VariationBasicFieldsProps) => {
  const { samplingCampaignRequest } = useSamplingCampaign();
  const { control } = useFormContext<EditableBVSamplingProduct>();
  const variations = useWatch({ control, name: 'variations' });
  const hasVariations = variations.length > 1;

  const variationRules = getVariationsValidationRules(
    0,
    variations.length,
    true,
  );

  if (hasVariations) {
    return null;
  }

  const currency = samplingCampaignRequest?.currency ?? 'USD';
  const currencySymbol = getRevenueSymbolFromFormat(currency, false);
  const currencyInstructions =
    currencySymbol && currencySymbol !== currency
      ? `${currency} (${currencySymbol})`
      : currency;

  return (
    <Container>
      {/* Quantity Field */}
      <FormField
        inputType='text'
        instructions='Total products'
        isViewOnly={isViewOnly}
        label={ProductDetailsFieldLabels.quantity}
        name='variations.0.quantity'
        placeholder='1,000'
        rules={variationRules['variations.0.quantity']}
        width='half'
      />

      {/* FMV Field */}
      <FormField
        inputType='text'
        instructions={currencyInstructions}
        isViewOnly={isViewOnly}
        label={ProductDetailsFieldLabels.fairMarketValue}
        name='variations.0.fairMarketValue'
        placeholder='25.99'
        rules={variationRules['variations.0.fairMarketValue']}
        width='half'
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[3]};
`;
